import { Check, Moon, Sun } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";
import { useTheme } from "./use-theme";

/** The three theme choices, shared by `ThemeModeToggle`'s own dropdown and `ThemeModeMenuItems`' mobile copy. */
function ThemeModeItems() {
  const { theme, setTheme } = useTheme();

  return (
    <>
      <DropdownMenuItem onClick={() => setTheme("light")}>
        Light
        <Check className={cn("ml-auto", theme !== "light" && "hidden")} />
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => setTheme("dark")}>
        Dark
        <Check className={cn("ml-auto", theme !== "dark" && "hidden")} />
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => setTheme("system")}>
        System
        <Check className={cn("ml-auto", theme !== "system" && "hidden")} />
      </DropdownMenuItem>
    </>
  );
}

/**
 * Toggle button that lets users switch between light, dark, and system UI themes.
 *
 * User's selection is persisted using the store.
 * Automatically included in the default Layout component header.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/thememodetoggle ThemeModeToggle documentation}
 */
export function ThemeModeToggle() {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {/* `md:inline-flex` (not `sm:`) — matches `useIsMobile`'s own 768px
            breakpoint (`hooks/use-mobile.ts`), the one `Layout` uses to
            decide whether to mount `ThemeModeMenuItems` instead. Mismatched
            breakpoints would leave a width range showing both controls (or
            neither) — see `ThemeModeMenuItems`'s own note. */}
        <Button variant="ghost" size="icon" className="hidden md:inline-flex">
          <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <ThemeModeItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The same theme options as `ThemeModeToggle`, embedded inside another
 * already-open dropdown (`UserMenu`, via its `children` slot) instead of
 * behind their own trigger button. `ThemeModeToggle`'s own trigger is
 * deliberately hidden below the `md` breakpoint (header real estate), which
 * left no way to change the theme at all on a phone-sized screen (ticket
 * feedback: "how to change mode on mobile? ... put to dropdown when i tap
 * on avatar in top right corner"). `Layout` only passes this as `UserMenu`'s
 * `children` when `useIsMobile()` is true — not a CSS-only `hidden`/`block`
 * toggle here, since `UserMenu` decides whether to render its own separator
 * based on whether it *received* children at all (`Children.count`), which
 * a CSS class can't affect; conditionally mounting from the parent keeps
 * that check correct on desktop (no stray empty separator) as well as
 * mobile.
 */
export function ThemeModeMenuItems() {
  return (
    <DropdownMenuGroup>
      <ThemeModeItems />
      <DropdownMenuSeparator />
    </DropdownMenuGroup>
  );
}
